
import Notification from '@/components/helper/Notification.vue'
import JigTextField from '@/components/input/JigTextField.vue'
import LabelWithHint from '@/components/input/LabelWithHint.vue'
import ImageUploader from '@/components/modals/ImageUploader.vue'
import PromptYesNo from '@/components/modals/PromptYesNo.vue'
import { SubscriptionConst, TenantConst } from '@/constants'
import SubscriptionCancelation from '@/mixin/SubscriptionCancelation'
import { BillingSource, BillingStatus, InvoiceStatus, PaymentIntentStatus, SubscriptionStatus } from '@/modules/subscription/types'
import { Permissions } from '@/security/permissions'
import { PermissionActions } from '@/store/constants'
import { FlexFeatureField, Tenant, TenantSetupJob, TenantSubscription } from '@/store/modules/app/types'
import { DynaconfConfig, Namespace, PricignListItem, PricingTier, UploadTenantLogoRequest } from '@/store/types'
import { AppHelper } from '@/utils/app-helper'
import { DateFormatter } from '@/utils/date-formatter'
import { HSHelpers } from '@/utils/hs-helpers'
import { ValidationRules } from '@/utils/input-validation'
import { ColorKey, TenantHelpers } from '@/utils/tenant-helpers'
import { ValidationRulesType } from '@/utils/type'
import IconLockSolid from '@svg/IconLockSolid.svg'
import IconUser from '@svg/IconUser.svg'
import { Component, Mixins, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, Mutation, State } from 'vuex-class'

declare var consoleLog: any

@Component({
  components: {
    'prompt-yes-no': PromptYesNo,
    'image-uploader': ImageUploader,
    'jig-text-field': JigTextField,
    'label-with-hint': LabelWithHint,
    notification: Notification,
    IconLockSolid,
    IconUser,
  },
})
export default class TenantVue extends Mixins(SubscriptionCancelation) {
  @State('apiError', { namespace: Namespace.Utils })
  public apiError!: string
  @State('myTenantIndex', { namespace: Namespace.App })
  public myTenantIndex!: number
  @State('myTenants', { namespace: Namespace.App })
  public myTenants!: Tenant[]
  @State('myTenant', { namespace: Namespace.App })
  public myTenant!: Tenant
  @State('tenantSetupJob', { namespace: Namespace.App })
  public tenantSetupJob!: TenantSetupJob
  @State('dynaconfConfig', { namespace: Namespace.App })
  public dynaconfConfig!: DynaconfConfig
  @State('isSuperUser', { namespace: Namespace.App })
  public isSuperUser!: boolean

  @Getter('isPreparingApp', { namespace: Namespace.Utils })
  public isPreparingApp!: boolean

  @Mutation('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenantState: any
  @Mutation('updateTenant', { namespace: Namespace.App })
  public updateTenant: any

  @Action('loadMyTenant', { namespace: Namespace.App })
  public loadMyTenant: any
  @Action('loadMyTenantLogo', { namespace: Namespace.App })
  public loadMyTenantLogo: any
  @Action('uploadTenantLogoImage', { namespace: Namespace.App })
  public uploadTenantLogoImage: any
  @Action('updateMyTenant', { namespace: Namespace.App })
  public updateMyTenant: any
  @Action('updateMyTenantsWithNewTenant', { namespace: Namespace.App })
  public updateMyTenantsWithNewTenant: any
  @Action('deleteTenantById', { namespace: Namespace.App })
  public deleteTenantById: any
  @Action('setMyTenant', { namespace: Namespace.App })
  public setMyTenant: any
  @Action('getTenantSetupJob', { namespace: Namespace.App })
  public getTenantSetupJob: any
  @Action('completeTenantSetupJob', { namespace: Namespace.App })
  public completeTenantSetupJob: any
  @Action('getSubscription', { namespace: Namespace.App })
  public getSubscription: any
  @Action('getDynaconfConfig', { namespace: Namespace.App })
  public getDynaconfConfig: any
  @Action('loadTenantUseID', { namespace: Namespace.App })
  public loadTenantUseID: any
  @Action('syncAuth0SSOConfig', { namespace: Namespace.App })
  public syncAuth0SSOConfig: any

  public $refs!: Vue['$refs'] & {
    logoImageUploader: ImageUploader
    deletePromptYesNo: PromptYesNo
    savePromptYesNo: PromptYesNo
    cancelSubscriptionPromptYesNo: PromptYesNo
  }

  private readonly saveChangesSuccessMsg = 'Team changes saved'
  private readonly defaultColor1Hex = TenantHelpers.TenantDefaultColor1Hex

  private editedTenant!: Tenant
  private formIsValid: boolean = false
  private nameRules!: any
  private urlRules!: any
  private noneNegativeNumberRules!: any
  private snackbarVisible: boolean = false
  private snackbarMessage: string = this.saveChangesSuccessMsg
  private isSaving: boolean = false

  private isUserBillingContact: boolean = false
  private isAppleBillingSource: boolean = false
  private isStripeBillingSource: boolean = false
  private isNoneBillingSource: boolean = false
  private tokenBillingStatus!: string
  private openHSChatFailed: boolean = false

  private subscriptionStatusColor: string = ''
  private subscriptionInvoiceStatusColor: string = ''
  private subscriptionInvoicePaymentIntentStatusColor: string = ''
  private subscriptionShowContactUs: boolean = false
  private subscriptionStatusLabels = new Map([
    [SubscriptionStatus.Active, 'Active'],
    [SubscriptionStatus.Trialing, 'Trialing'],
    [SubscriptionStatus.IncompleteExpired, 'Requires Attention'],
    [SubscriptionStatus.Incomplete, 'Requires Attention'],
    [SubscriptionStatus.PastDue, 'Requires Attention'],
    [SubscriptionStatus.Unpaid, 'Requires Attention'],
    [SubscriptionStatus.Canceled, 'Canceled'],
    [BillingStatus.Paid, 'Paid'],
    [BillingStatus.Trial, 'Trialing'],
    [BillingStatus.BillingIssuesDetected, 'Requires Attention'],
    [BillingStatus.Expired, 'Expired'],
    [BillingStatus.UserUnsubscribed, 'User Unsubscribed'],
    [BillingStatus.NeverSubscribed, 'Not Found'],
    [BillingStatus.NewUnknownStatus, 'Internal Error'],
  ])
  private currentSubscription: TenantSubscription = SubscriptionConst.defaultSubscription
  private subscriptionTiersValue: SubscriptionConst.Subscription[] = SubscriptionConst.subscriptionTiers
  private subscriptionManagedByTypeValue: any[] = SubscriptionConst.subscriptionManagedByTypes
  private pricingSetsValue: SubscriptionConst.Subscription[] = SubscriptionConst.pricingSets // fallback default value
  private dynaconfPricingListNames: SubscriptionConst.Subscription[] = []
  private allowMovoJigs: boolean = false
  private allowJigStats: boolean = false
  private AVPFeatureGateEnabled: boolean = true

  private customFeaturesDefaultValues: Record<string, any> = {}
  private customFeaturesModels: Record<string, any> = {}
  private customFeaturesFlexSwitches: Record<string, FlexFeatureField> = {}
  private customFeaturesFlexTextFields: Record<string, FlexFeatureField> = {}

  private disabledTextField: string = '-1'
  private disabledSwitchField: boolean = false

  private isSyncingSSO: boolean = false

  constructor() {
    super()
    this.nameRules = ValidationRules.RequiredName
    this.urlRules = ValidationRules.OptionalUrl
    this.noneNegativeNumberRules = ValidationRules.RequiredNoneNegativeNumber

    this.currentSubscription.SubscriptionFeatures.avp.gatedFeatures = {
      [SubscriptionConst.Features.AVPFullAccessFeatureGate]: {
        name: 'ViewingGate_MAR_2024',
        enforce: false,
      },
    }
    this.editedTenant = { ...TenantConst.defaultTenant }
  }

  @Watch('myTenantIndex')
  private onMyTenantIndexPropertyChanged(value: number, oldValue: number) {
    this.loadData()
    this.$refs.logoImageUploader.ClearImageData()
  }

  protected async mounted() {
    this.$refs.logoImageUploader.Init(ValidationRules.PngImageUpload)
    this.loadData()
  }

  private get additionalSeatsRules(): ValidationRulesType {
    return [
      ...ValidationRules.RequiredNumber,
      (v: any) =>
        this.editedTenant.Subscription.SeatsIncludedInTier + v > 0 || 'The sum of seats included and additional seats should be positive',
    ]
  }

  private get dialogActiveLabel(): string {
    return this.editedTenant.Active ? 'Active' : 'Inactive'
  }

  private get allowMovoJigsLabel(): string {
    return this.switchFieldLabel('Movo Jigs', this.allowMovoJigs)
  }

  private get allowJigStatsLabel(): string {
    return this.switchFieldLabel('Jig Stats', this.allowJigStats)
  }

  private get AVPFeatureGateLabel(): string {
    return this.switchFieldLabel('Full AVP access', !this.AVPFeatureGateEnabled)
  }

  // classic pricing set: set_2020_01
  private get isClassicPricingSet() {
    return this.editedTenant.Subscription != null && !this.dynaconfPricingListNames.includes(this.editedTenant.Subscription.PricingSet)
  }

  private get isPricingSetWithSeatsOrMaxJigs(): boolean {
    const targetPricingSet = this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet]

    return (
      targetPricingSet &&
      this.editedTenant.Subscription.TierType != '' &&
      (targetPricingSet.tiers[this.editedTenant.Subscription.TierType].maxJigs != null ||
        targetPricingSet.tiers[this.editedTenant.Subscription.TierType].seatsIncluded != null)
    )
  }

  private get isMaxJigsSet(): boolean {
    const targetPricingSet = this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet]
    // When MaxJigs === 0 it means unlimited
    return (
      this.isPricingSetWithSeatsOrMaxJigs &&
      targetPricingSet.tiers[this.editedTenant.Subscription.TierType].maxJigs != null &&
      this.editedTenant.Subscription.MaxJigs !== 0
    )
  }

  private get subscriptionStatusDisplay() {
    if (this.isAppleBillingSource) {
      return this.subscriptionStatusLabels.get(this.tokenBillingStatus as string)
    } else {
      return this.subscriptionStatusLabels.get(this.subscription.status as string)
    }
  }

  private get isSubscriptionNotActiveButHasInvoice() {
    return this.subscription.status !== SubscriptionStatus.Active && this.subscription.latest_invoice
  }

  private get isSubscriptionPaymentIntentRequiresAction() {
    if (this.subscription.latest_invoice.payment_intent) {
      return this.subscription.latest_invoice.payment_intent.status === PaymentIntentStatus.RequiresAction
    } else {
      return false
    }
  }

  private get isSubscriptionTrialling() {
    return this.subscription.status === SubscriptionStatus.Trialing
  }

  private get trialEndDisplay() {
    // trial_end is a unix timestamp, in seconds.
    return DateFormatter.formatSubscriptionDate(this.subscription.trial_end) // Eg "Tomorrow at 10:12 AM" or "Sunday at 2:30 AM" or if longer than 1 week away "7/10/2020"
  }

  private get deleteButtonDisabled(): boolean {
    let disabled = this.customBrandingDisabled
    if (!disabled) {
      // Don’t allow deletion of tenants that have existing users
      disabled = this.editedTenant.UserCount === undefined || this.editedTenant.UserCount > 0
    }
    return disabled
  }

  private get allowCustombranding(): boolean {
    return this.editedTenant.Subscription.AllowCustomBranding
  }

  private get editableCRMFields(): string[] {
    return TenantConst.CRMEditableFields
  }

  private get readonlyCRMFields(): string[] {
    return Object.keys(this.editedTenant.CRM.Available).filter((key) => !TenantConst.CRMEditableFields.includes(key))
  }

  // User can update branding (team settings) when:
  // 1. user is a Super User (JigStaff only)
  // 2. AllowCustomBranding is true and user has team manage permission (Team Manager / Team Admin)
  private get customBrandingDisabled(): boolean {
    if (this.editedTenant.ID === TenantHelpers.InvalidTenantID) {
      return true
    }

    if (!this.allowCustombranding && !this.isSuperUser) {
      return true
    }

    // If we are not a super user and we don't have manage tenant permissions, disable saving.
    const tenantTokens = this.$auth0.tenants
    const tenantToken = TenantHelpers.GetTenantTokenByTenantID(tenantTokens, this.editedTenant.ID)
    if (
      !this.isSuperUser &&
      (tenantToken === undefined || !Permissions.TokenHasPermissionAction(tenantToken, Permissions.PermTenant, PermissionActions.Manage))
    ) {
      return true
    }

    return this.$refs.logoImageUploader.IsShowingDialog || this.isSaving
  }

  // Disable the team field and active/inactive switcher when it's JigSpaceStaff tenant.
  private get isTenantJigSpaceStaff(): boolean {
    return TenantHelpers.IsTenantJigSpaceStaff(this.editedTenant)
  }

  private get tenantNotes(): string {
    return this.allowCustombranding
      ? 'People viewing your Jigs will go to this link if they click on your logo.'
      : 'Upgrade your plan to white label the JigSpace viewers.'
  }

  private get availableFlexFields() {
    if (this.editedTenant.Subscription.SubscriptionFeatures.Available == null) {
      return {}
    }

    return this.editedTenant.Subscription.SubscriptionFeatures.Available
  }

  private get defaultPricingSetConfig(): any {
    if (this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet] == null) {
      return {}
    }

    return this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet].tiers[this.editedTenant.Subscription.TierType]
  }

  private get totalSeats(): number {
    if (this.editedTenant == null || this.editedTenant.Subscription == null) {
      return -1
    }

    // Defatult tenant object seats included value is string '-' character
    const seatsIncluded: number =
      typeof this.editedTenant.Subscription.SeatsIncludedInTier !== 'number' ? 0 : this.editedTenant.Subscription.SeatsIncludedInTier
    const additionalSeats: number =
      typeof this.editedTenant.Subscription.AdditionalSeats !== 'number' ? 0 : this.editedTenant.Subscription.AdditionalSeats

    return seatsIncluded + additionalSeats
  }

  private get targetTenant(): Tenant | undefined {
    if (this.myTenants.length === 0 || this.myTenantIndex === TenantHelpers.InvalidTenantIndex) {
      return
    }

    return this.myTenants[this.myTenantIndex]
  }

  private async loadData() {
    this.refreshEditedTenant()
    if (this.editedTenant.Logourl && this.editedTenant.Logourl.length > 0) {
      await this.loadMyTenantLogo(this.editedTenant)
      this.refreshEditedTenant()
    }

    await this.loadTenantUseID({ tenantId: this.editedTenant.ID })

    if (Object.keys(this.dynaconfConfig.pricing.sets).length === 0) {
      await this.getDynaconfConfig()
    }

    if (Object.keys(this.dynaconfConfig.pricing.sets).length > 0) {
      this.dynaconfPricingListNames = Object.keys(this.dynaconfConfig.pricing.sets) as SubscriptionConst.Subscription[]
      this.pricingSetsValue = [SubscriptionConst.Subscription.pricingSet_2020_01, ...this.dynaconfPricingListNames]
    }

    this.loadSubscription()
    this.currentSubscription = {
      ...this.editedTenant.Subscription,
    }

    // Refresh the default values after dynaconf is loaded
    this.updateCustomFeaturesDefaultValues()
  }

  private openLogoUploadDialog(tenant: any) {
    this.editedTenant = Object.assign({}, tenant as Tenant)
    this.editedTenant.LogoUpdated = false

    this.$refs.logoImageUploader.Show(
      null,
      (file: File): Promise<boolean> =>
        this.uploadTenantLogoImage({
          TenantId: this.editedTenant.ID,
          Image: file,
        } as UploadTenantLogoRequest),
      (): any => this.onPostLogoUpload()
    )
  }

  private showCancelSubscriptionPrompt() {
    this.$refs.cancelSubscriptionPromptYesNo.ShowPrompt()
  }

  private showDeleteTenantPrompt(t: Tenant) {
    this.$refs.deletePromptYesNo.ShowPrompt()
  }

  private async deleteTenant(t: Tenant) {
    await this.deleteTenantById(t.ID)
    if (this.apiError.length === 0) {
      this.setMyTenant({
        TenantId: TenantHelpers.GetJigSpaceStaffTenantID(),
      })
      this.$router.push('/tenants')
    }
  }

  private showSaveTenantPrompt(t: Tenant) {
    this.$refs.savePromptYesNo.ShowPrompt()
  }

  private async onSavePromptSuccess(updatedTenant: Tenant) {
    this.saveChanges(updatedTenant)
  }

  private async saveChanges(updatedTenant: Tenant) {
    if (this.customBrandingDisabled) {
      return
    }

    this.isSaving = true
    this.snackbarVisible = false

    const tenantCopy = Object.assign({}, updatedTenant)

    tenantCopy.ColorHex1 = this.removeAlphaFromHexColor(tenantCopy.ColorHex1)
    tenantCopy.ColorHex2 = this.removeAlphaFromHexColor(tenantCopy.ColorHex2)

    const tenantPricingSet: PricignListItem = this.dynaconfConfig.pricing.sets[updatedTenant.Subscription.PricingSet]

    if (tenantPricingSet) {
      const tenantPricingSetTier: PricingTier = tenantPricingSet.tiers[updatedTenant.Subscription.TierType]

      if (tenantPricingSetTier.seatsIncluded != null) {
        updatedTenant.Subscription.SeatsIncludedInTier = tenantPricingSetTier.seatsIncluded
      } else {
        // API will handle the correct SeatsIncludedInTier number.
        updatedTenant.Subscription.SeatsIncludedInTier = SubscriptionConst.Subscription.defaultSeatsIncludedInTier
      }
      if (tenantPricingSetTier.maxJigs != null) {
        updatedTenant.Subscription.MaxJigs = tenantPricingSetTier.maxJigs
      } else {
        // API will handle the correct MaxJigs.
        updatedTenant.Subscription.MaxJigs = SubscriptionConst.Subscription.defaultMaxJigsInTier
      }
      if (tenantPricingSetTier.maxJigsCountPer != null) {
        updatedTenant.Subscription.MaxJigsCountPer = tenantPricingSetTier.maxJigsCountPer
      } else {
        // API will handle the correct MaxJigsCountPer.
        updatedTenant.Subscription.MaxJigsCountPer = SubscriptionConst.Subscription.defaultMaxJigsPerInTier
      }
    }

    if (updatedTenant.Subscription.AdditionalSeats && typeof updatedTenant.Subscription.AdditionalSeats !== 'number') {
      updatedTenant.Subscription.AdditionalSeats = parseInt(updatedTenant.Subscription.AdditionalSeats)
    }

    // Append url protocol if there isn't one.
    if (tenantCopy.JigFinishedCustomLink && !tenantCopy.JigFinishedCustomLink.includes('://')) {
      tenantCopy.JigFinishedCustomLink = `http://${tenantCopy.JigFinishedCustomLink}`
    }

    // Update features
    // For AVP full access, we follow following logic:
    // `Full AVP access enabled` === `AVPFeatureGate not shown` === enabled: true, enforce: false`
    // `Full AVP access disabled` === `AVPFeatureGate shown` === enabled: false, enforce: true`
    tenantCopy.Subscription.SubscriptionFeatures.avp.enabled = !this.AVPFeatureGateEnabled
    if (tenantCopy.Subscription.SubscriptionFeatures.avp.gatedFeatures) {
      tenantCopy.Subscription.SubscriptionFeatures.avp.gatedFeatures[SubscriptionConst.Features.AVPFullAccessFeatureGate].enforce =
        this.AVPFeatureGateEnabled
    }
    tenantCopy.Subscription.SubscriptionFeatures.movo.enabled = this.allowMovoJigs
    tenantCopy.Subscription.SubscriptionFeatures.stats.jigStats.enabled = this.allowJigStats

    // Update legacy values. When API is fixed we can remove them.
    tenantCopy.Subscription.AllowMovoJigs = this.allowMovoJigs
    tenantCopy.Subscription.AllowJigStats = this.allowJigStats

    for (const key in this.customFeaturesModels) {
      const defaultValue: any = this.customFeaturesDefaultValues[key]
      let value: any = this.customFeaturesModels[key]
      const featureField: FlexFeatureField = updatedTenant.Subscription.SubscriptionFeatures.Available[key]

      switch (featureField.Type) {
        case SubscriptionConst.FlexFeatureFieldType.int:
          value = parseInt(value, 10)
          break
        case SubscriptionConst.FlexFeatureFieldType.float:
          value = parseFloat(value)
          break
        default:
          break
      }

      // Remove the custom value if it matches the pricing set's default
      if (value === defaultValue) {
        delete updatedTenant.Subscription.SubscriptionFeatures.CustomFeatures[key]
        continue
      }

      updatedTenant.Subscription.SubscriptionFeatures.CustomFeatures[key] = value
    }

    await this.updateMyTenant(tenantCopy)

    if (this.apiError === undefined || this.apiError.length === 0) {
      this.snackbarVisible = true
      await this.loadMyTenant({ TenantId: this.editedTenant.ID })
      this.editedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.myTenant)

      if (this.isSuperUser) {
        this.updateMyTenantsWithNewTenant(this.editedTenant)
      }
    }

    this.refreshEditedTenant()
    this.isSaving = false
  }

  private removeAlphaFromHexColor(hex: string): string {
    if (hex.length > 7) {
      hex = hex.substring(1, 7)
    }
    return hex
  }

  private async onPostLogoUpload() {
    this.$refs.logoImageUploader.Hide()

    // We've just finished uploading a new logo url but so far our screen doesn't know about it's result.
    // Pull down the latest tenant data and update our local copy so we get the new Logourl.
    await this.loadMyTenant({ TenantId: this.editedTenant.ID })
    this.editedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.myTenant)

    this.editedTenant.LogoUpdated = true
    this.updateMyTenantState(this.editedTenant)
    if (this.isSuperUser) {
      this.updateTenant(this.editedTenant)
      this.updateMyTenantsWithNewTenant(this.editedTenant)
    }
    this.refreshEditedTenant()

    // Does this tenant have an uploaded logo to download?
    if (this.editedTenant.Logourl !== '') {
      // Get the tenant logo and update our local tenant copy again
      await this.loadMyTenantLogo(this.editedTenant)
      this.refreshEditedTenant()
    }

    // To update the logo in other places (like the drawer), we have to trigger the watch on myTenant which is pretty hacky but still an improvement.
    var oldValue = this.$store.state.app.myTenant
    this.$store.state.app.myTenant = undefined
    this.$store.state.app.myTenant = oldValue
  }

  private refreshEditedTenant() {
    if (this.targetTenant == null) {
      return
    }

    // Use JSON.stringfy and JSON.parse for a deep copy of the value not reference.
    // So that any unsaved changes won't update the original `tenant` item of `state.myTenants` array and `state.myTenant` object.
    // When using Object.assign, top-level properties are assigned by value, however nested objects/references are still shared between original `tenant` and `editedTenant`
    // Note that as our tenant object is relatively simple, we can use JSON parse/stringify. If tenant object contains non-JSON-safe value (e.g., functions, undefined, NaN).
    this.editedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.targetTenant)

    this.allowMovoJigs = this.editedTenant.Subscription.SubscriptionFeatures.movo.enabled
    this.allowJigStats = this.editedTenant.Subscription.SubscriptionFeatures.stats.jigStats.enabled
    // By default for new tenants or existing tenants that
    // haven't updated this flag before
    // `enabled` and `enforce` values will both be `true`.
    // We check `enforce` value for actual Featuregated status.
    this.AVPFeatureGateEnabled =
      this.editedTenant.Subscription.SubscriptionFeatures.avp != null &&
      this.editedTenant.Subscription.SubscriptionFeatures.avp.gatedFeatures != null &&
      this.editedTenant.Subscription.SubscriptionFeatures.avp.gatedFeatures.ViewingGate_MAR_2024.enforce

    // Correct any invalid colors set as "" or "      "
    if (!this.editedTenant.ColorHex1 || !this.editedTenant.ColorHex1.trim()) {
      this.editedTenant.ColorHex1 = TenantHelpers.TenantDefaultColor1Hex
    }
    if (!this.editedTenant.ColorHex2 || !this.editedTenant.ColorHex2.trim()) {
      this.editedTenant.ColorHex2 = TenantHelpers.TenantDefaultColor2Hex
    }

    if (this.editedTenant.Subscription.SubscriptionFeatures.Available == null) {
      return
    }

    // Iterate over all available flex fields, separate out
    // switch fields and text-input fields.
    // Also exclude special field `MaxJigs` as it needs to siti in a special layout.
    for (const key in this.editedTenant.Subscription.SubscriptionFeatures.Available) {
      const field: FlexFeatureField = this.editedTenant.Subscription.SubscriptionFeatures.Available[key]

      if (field.Type === SubscriptionConst.FlexFeatureFieldType.bool) {
        this.customFeaturesFlexSwitches[key] = field
      } else if (key.toLowerCase() !== 'maxjigs') {
        // We are excluding MaxJigs here because it has a special layout.
        // We render everything else as dynamic fields.
        this.customFeaturesFlexTextFields[key] = field
      }

      // Set the v-model variable using `customFeaturesModels`
      if (field.SyncField && this.editedTenant.Subscription.SubscriptionFeatures.CustomFeatures[field.SyncField] != null) {
        // If custom value is set use the value from CustomFeatures
        this.customFeaturesModels[key] = this.editedTenant.Subscription.SubscriptionFeatures.CustomFeatures[field.SyncField]
      } else if (field.SyncField && this.editedTenant.Subscription[field.SyncField] != null) {
        // If no custom value is set, use the value from Sync field.
        this.customFeaturesModels[key] = this.editedTenant.Subscription[field.SyncField]
      } else {
        // If SyncField is not available, then the value will be available in CustomFeatures.
        this.customFeaturesModels[key] = this.editedTenant.Subscription.SubscriptionFeatures.CustomFeatures[key]
      }

      if (Object.keys(this.defaultPricingSetConfig).length > 0) {
        this.updateCustomFeatureDefaultValue(field, key)
      }
    }
  }

  private updateCustomFeaturesDefaultValues() {
    for (const key in this.editedTenant.Subscription.SubscriptionFeatures.Available) {
      const field: FlexFeatureField = this.editedTenant.Subscription.SubscriptionFeatures.Available[key]

      this.updateCustomFeatureDefaultValue(field, key)
    }
  }

  private updateCustomFeatureDefaultValue(field: FlexFeatureField, key: string) {
    // Save the default value using `customFeaturesDefaultValues`
    if (field.SyncField) {
      // Since dynaconf returns camelCase and our
      // flex fields keys are in SentenseCase,
      // we need to check key value in both format.
      // For example `TeamStorageLimitMB` and `teamStorageLimitMB`, `MaxJigs` and `maxJigs`.
      this.customFeaturesDefaultValues[key] =
        this.defaultPricingSetConfig[field.SyncField] != null
          ? this.defaultPricingSetConfig[field.SyncField]
          : this.defaultPricingSetConfig[AppHelper.sentenceToCamelCase(field.SyncField)]
    } else {
      this.customFeaturesDefaultValues[key] = this.editedTenant.Subscription.SubscriptionFeatures.Available[key].Default
    }
  }

  private switchFieldLabel(labelCopy: string, enabled: boolean) {
    return enabled ? `${labelCopy} enabled` : `${labelCopy} disabled`
  }

  private flexFieldLabel(key: string) {
    return SubscriptionConst.CustomFeaturesLabels[key]
  }

  private flexFieldAttrs(field: FlexFeatureField) {
    switch (field.Type) {
      case 'int':
        return {
          rules: this.noneNegativeNumberRules,
        }
      case 'float':
        return {
          rules: this.noneNegativeNumberRules,
        }
      default:
        return {}
    }
  }

  private flexFieldLabelComponent(field: FlexFeatureField, key: string) {
    if (!field) {
      return {
        component: 'label',
        attrs: {
          for: key,
        },
      }
    }

    if (!field.Tooltip) {
      return {
        component: 'label',
        attrs: {
          for: key,
          'v-html': field.Label || this.flexFieldLabel(key),
        },
      }
    }

    return {
      component: 'label-with-hint',
      attrs: {
        labelFor: key,
        labelCopy: field.Label || this.flexFieldLabel(key),
        refKey: `${key}Tip`,
        tipCopy: field.Tooltip,
      },
    }
  }

  private async loadSubscription() {
    this.IsLoadingSubscription = true
    this.isUserBillingContact = false
    this.isAppleBillingSource = false
    this.isStripeBillingSource = false
    this.isNoneBillingSource = false

    var tenantTokens = this.$auth0.tenants
    var thisTenantToken = TenantHelpers.GetTenantTokenByTenantID(tenantTokens, this.editedTenant.ID)
    if (thisTenantToken === undefined) {
      this.IsLoadingSubscription = false
      return
    }

    this.isUserBillingContact = Permissions.TokenHasPermissionAction(
      thisTenantToken,
      Permissions.PermTenant,
      PermissionActions.BillingContact
    )
    this.isAppleBillingSource = thisTenantToken.billing_source === BillingSource.Apple
    this.isStripeBillingSource = thisTenantToken.billing_source === BillingSource.Stripe
    this.isNoneBillingSource = thisTenantToken.billing_source === BillingSource.None

    if (this.isUserBillingContact) {
      if (this.isAppleBillingSource) {
        // If the tenant is a billing source apple, we direct them to apple subscription page to manage it.
        // https://apps.apple/account/subscriptions
        this.tokenBillingStatus = thisTenantToken.billing_status
        var logMsg = `Subscription Status = ${this.tokenBillingStatus}`
        if (this.tokenBillingStatus === BillingStatus.Paid || this.tokenBillingStatus === BillingStatus.Trial) {
          this.subscriptionStatusColor = 'green'
        } else {
          this.subscriptionStatusColor = 'red'
        }
      } else if (this.isStripeBillingSource) {
        if (
          await this.getSubscriptionWithInvoice({
            customerEmail: this.$auth0.user.email,
            tenantId: this.editedTenant.ID,
          })
        ) {
          var logMsg = `Subscription Status = ${this.subscription.status} `
          if (this.subscription.status === SubscriptionStatus.Active || this.subscription.status === SubscriptionStatus.Trialing) {
            this.subscriptionStatusColor = 'green'
          } else {
            this.subscriptionStatusColor = 'red'
            this.subscriptionShowContactUs =
              this.subscription.status === SubscriptionStatus.IncompleteExpired ||
              this.subscription.status === SubscriptionStatus.Canceled ||
              this.subscription.status === SubscriptionStatus.Unpaid
          }
          if (this.subscription.latest_invoice) {
            logMsg += `Latest Invoice Status = ${this.subscription.latest_invoice.status} `
            if (this.subscription.latest_invoice.status === InvoiceStatus.Paid) {
              this.subscriptionInvoiceStatusColor = 'green'
            } else {
              this.subscriptionInvoiceStatusColor = 'red'
            }
          }
          if (this.subscription.latest_invoice && this.subscription.latest_invoice.payment_intent) {
            logMsg += `Payment Intent Status = ${this.subscription.latest_invoice.payment_intent.status} `
            if (this.subscription.latest_invoice.payment_intent.status === PaymentIntentStatus.Succeeded) {
              this.subscriptionInvoicePaymentIntentStatusColor = 'green'
            } else {
              this.subscriptionInvoicePaymentIntentStatusColor = 'red'
            }
          }
          consoleLog(logMsg)
        }
        if (
          this.subscription.status === SubscriptionStatus.Active &&
          (await this.getTenantSetupJob({ userEmail: this.$auth0.user.email })) &&
          this.tenantSetupJob.status !== '' &&
          this.tenantSetupJob.createdTenantId === this.editedTenant.ID // Just in case this user is currently setting up a different tenant, check the id's are the same.
        ) {
          // The subscription is active, but there's still a tenant-setup-job, so complete it.
          if (await this.completeTenantSetupJob()) {
            await this.loadSubscription()
          }
        }
      }
    } else {
      consoleLog(`No subscription available because user is not a billing contact. Billing Source is ${thisTenantToken.billing_source}`)
      // Until we have the billing contact set as a variable in the tenant, so we know their email, we can't get the subscription status from stripe
      // Only the billing_contact themselves can.
      // if (await this.getSubscription({ customerEmail: this.editedTenant.billingContactEmail, tenantId: this.editedTenant.ID })) {
      //   consoleLog(JSON.stringify(this.subscription))
      //   debugger
      // }
    }
    this.IsLoadingSubscription = false
  }

  private closeSnackbar() {
    this.snackbarVisible = false
  }

  private resetColorToDefault(tenant: Tenant, key: ColorKey) {
    if (key === 'Color1') {
      tenant.ColorHex1 = TenantHelpers.TenantDefaultColor1
    } else if (key === 'Color2') {
      tenant.ColorHex2 = TenantHelpers.TenantDefaultColor2
    }
  }

  private openHSChat() {
    this.openHSChatFailed = !HSHelpers.OpenHSChat()
  }

  private updateDataFromTier() {
    // If subscription tier has changed, show `-` as Seats Included,
    // as client side has no details of seats included number,
    // it's handled by API. If subscription tier changed back to initial
    // value, show original SeatsIncluded number.
    if (this.editedTenant.Subscription.TierType === this.currentSubscription.TierType) {
      this.editedTenant.Subscription.SeatsIncludedInTier = this.currentSubscription.SeatsIncludedInTier
      this.editedTenant.Subscription.MaxJigs = this.currentSubscription.MaxJigs
      this.editedTenant.Subscription.MaxJigsCountPer = this.currentSubscription.MaxJigsCountPer
    } else {
      const tenantPricingSet: PricignListItem = this.dynaconfConfig.pricing.sets[this.editedTenant.Subscription.PricingSet]
      const tenantPricingSetTier: PricingTier = tenantPricingSet.tiers[this.editedTenant.Subscription.TierType]

      this.editedTenant.Subscription.SeatsIncludedInTier =
        tenantPricingSetTier.seatsIncluded || SubscriptionConst.Subscription.defaultSeatsIncludedInTier
      this.editedTenant.Subscription.MaxJigs = tenantPricingSetTier.maxJigs || SubscriptionConst.Subscription.defaultMaxJigsInTier
      this.editedTenant.Subscription.MaxJigsCountPer =
        tenantPricingSetTier.maxJigsCountPer || SubscriptionConst.Subscription.defaultMaxJigsPerInTier
    }
  }

  private async syncAuth0SSO() {
    this.isSyncingSSO = true
    await this.syncAuth0SSOConfig(this.editedTenant.ID)
    await this.loadMyTenant({ TenantId: this.editedTenant.ID })
    this.editedTenant = AppHelper.deepCopyNestedJsonSafeObject(this.myTenant)
    this.isSyncingSSO = false
  }
}
